import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { FileTextOutlined } from "@ant-design/icons";

import EstimatePdfPreview from "../Estimate/EstimatePdfPreview";
import { loadProtectedFileObjectUrl } from "./estimateClaimsApi";

/**
 * A protected file — the office's claim form, the carrier's response — readable
 * in place.
 *
 * Full width and tall, not a thumbnail: this sits in the results pane, which
 * has the room, and a document you have to click into to read is a document
 * nobody reads. The viewer (EstimatePdfPreview) brings its own Open and Download.
 *
 * The file comes through its access-checked endpoint (loadProtectedFileObjectUrl):
 * a spinner until it lands, and when it can't be shown — a TIFF or HEIC the
 * browser won't render, or a read that was refused — a plain link that
 * downloads instead.
 *
 * Stored names are hashes, so the viewer is given `label` + the extension.
 */
export default function ProtectedFilePreview({ url, fileName, label = "file", height = 560 }) {
  const [state, setState] = useState({ loading: true, url: null, kind: "other" });

  useEffect(
    function () {
      let cancelled = false;
      let objectUrl = null;
      setState({ loading: true, url: null, kind: "other" });
      loadProtectedFileObjectUrl(url, fileName).then(function (res) {
        if (cancelled) {
          if (res.url) URL.revokeObjectURL(res.url);
          return;
        }
        objectUrl = res.url;
        setState({ loading: false, url: res.url, kind: res.kind });
      });
      return function () {
        cancelled = true;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    },
    [url, fileName],
  );

  const ext = ((fileName || "").match(/\.[a-z0-9]+$/i) || [""])[0].toLowerCase();
  const name = label + ext;

  if (state.loading) {
    return (
      <div className="est-form-loading">
        <Spin size="small" /> Loading…
      </div>
    );
  }

  const canRender = !!state.url && (state.kind === "pdf" || state.kind === "image");
  if (!canRender) {
    return (
      <a
        className="est-view-form-link"
        href={state.url || url}
        target="_blank"
        rel="noopener noreferrer"
        download={state.url ? name : undefined}
      >
        <FileTextOutlined /> View {label.replace(/-/g, " ")}
      </a>
    );
  }

  return (
    <div className="est-view-form-viewer">
      <EstimatePdfPreview href={state.url} documentName={name} kind={state.kind} height={height} />
    </div>
  );
}
